import React, { useMemo, useState } from "react";
import {
  Folder, FolderOpen, FileIcon, FileVideo, FileAudio, FileText,
  FileWarning, ShieldAlert, Play, ChevronRight, ChevronDown,
} from "lucide-react";

/* ---------- helpers ---------- */

const VIDEO_EXT = ["mkv", "mp4", "avi", "mov", "wmv", "webm", "m4v", "ts", "m2ts", "mpg", "mpeg", "flv", "vob", "iso", "ifo", "bup"];
const AUDIO_EXT = ["mp3", "flac", "wav", "aac", "m4a", "ogg", "opus", "ac3", "dts"];
const SUB_EXT   = ["srt", "ass", "ssa", "sub", "idx", "vtt", "sup"];
const RISKY_EXT = ["exe", "scr", "bat", "vbs", "lnk", "msi", "cmd", "js", "jse", "ps1", "hta", "com", "pif", "reg"];

function extOf(path) {
  const m = String(path || "").toLowerCase().match(/\.([a-z0-9]{1,5})$/);
  return m ? m[1] : "";
}

function categorize(path) {
  const e = extOf(path);
  if (VIDEO_EXT.includes(e)) return "video";
  if (AUDIO_EXT.includes(e)) return "audio";
  if (SUB_EXT.includes(e))   return "sub";
  if (e === "nfo")           return "nfo";
  if (RISKY_EXT.includes(e)) return "risky";
  return "other";
}

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let s = Number(n), i = 0;
  while (s >= 1024 && i < u.length - 1) { s /= 1024; i++; }
  return `${s.toFixed(s >= 100 ? 0 : s >= 10 ? 1 : 2)} ${u[i]}`;
}

/** Build a nested tree from flat [{path:"a/b/c.mkv", size}, ...] */
function buildTree(files) {
  const root = { id: "", name: "", size: 0, children: {}, files: [] };
  for (const f of files) {
    const parts = String(f.path || "").split("/").filter(Boolean);
    if (!parts.length) continue;
    const filename = parts.pop();
    let node = root;
    node.size += f.size || 0;
    let pathSoFar = "";
    for (const p of parts) {
      pathSoFar = pathSoFar ? `${pathSoFar}/${p}` : p;
      if (!node.children[p]) {
        node.children[p] = { id: pathSoFar, name: p, size: 0, children: {}, files: [] };
      }
      node = node.children[p];
      node.size += f.size || 0;
    }
    node.files.push({ name: filename, size: f.size || 0, fullPath: f.path });
  }
  return root;
}

/** Iteratively flatten the tree honoring the `expanded` set.
 *  Returns rows like {kind:"folder"|"file", id, name, depth, size, ...} */
function flatten(root, expanded) {
  const rows = [];
  // Walk root's own files first (top-level loose files), then folders.
  const walk = (node, depth) => {
    const folders = Object.values(node.children).sort((a, b) => a.name.localeCompare(b.name));
    const looseFiles = [...node.files].sort((a, b) => b.size - a.size);
    for (const folder of folders) {
      const isOpen = expanded.has(folder.id);
      rows.push({
        kind: "folder",
        id: folder.id,
        name: folder.name,
        depth,
        size: folder.size,
        childCount: Object.keys(folder.children).length + folder.files.length,
        isOpen,
      });
      if (isOpen) walk(folder, depth + 1);
    }
    for (const f of looseFiles) {
      const cat = categorize(f.name);
      rows.push({
        kind: "file",
        id: `${node.id}::${f.name}`,
        name: f.name,
        fullPath: f.fullPath,
        depth,
        size: f.size,
        category: cat,
      });
    }
  };
  walk(root, 0);
  return rows;
}

/* ---------- file icon by category ---------- */

const FILE_ICON = {
  video: <FileVideo className="w-3.5 h-3.5 shrink-0 text-[var(--tt-primary)]" />,
  audio: <FileAudio className="w-3.5 h-3.5 shrink-0 text-cyan-400" />,
  sub:   <FileText className="w-3.5 h-3.5 shrink-0 text-emerald-400" />,
  nfo:   <FileWarning className="w-3.5 h-3.5 shrink-0 text-amber-400" />,
  risky: <ShieldAlert className="w-3.5 h-3.5 shrink-0 text-red-400" />,
  other: <FileIcon className="w-3.5 h-3.5 shrink-0 text-[var(--tt-text-faint)]" />,
};

/* ---------- breakdown bar ---------- */

const CATEGORY_LABEL = {
  video: "Video", audio: "Audio", sub: "Subs", nfo: "NFO", risky: "Risky", other: "Other",
};
const CATEGORY_COLOR = {
  video: "var(--tt-primary)", audio: "#22D3EE", sub: "#34D399",
  nfo: "#FBBF24", risky: "#F87171", other: "#71717A",
};

function BreakdownBar({ totals, total }) {
  const order = ["video", "audio", "sub", "nfo", "risky", "other"];
  const entries = order.filter((k) => totals[k] > 0);
  if (!entries.length || !total) return null;
  return (
    <div data-testid="files-breakdown">
      <div className="flex h-1.5 rounded-full overflow-hidden bg-white/5">
        {entries.map((k) => (
          <div
            key={k}
            style={{ width: `${(totals[k] / total) * 100}%`, background: CATEGORY_COLOR[k] }}
            title={`${CATEGORY_LABEL[k]} · ${fmtBytes(totals[k])}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
        {entries.map((k) => (
          <span key={k} className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[var(--tt-text-muted)]">
            <span className="w-2 h-2 rounded-sm" style={{ background: CATEGORY_COLOR[k] }} />
            {CATEGORY_LABEL[k]} · <span className="font-mono-tt normal-case text-white/70">{fmtBytes(totals[k])}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- main exported component ---------- */

export default function TorrentFilesTree({ files, onViewNfo }) {
  const tree = useMemo(() => buildTree(files || []), [files]);

  const { totals, total, mainPlayablePath, risky, initialOpen } = useMemo(() => {
    const tot = { video: 0, audio: 0, sub: 0, nfo: 0, risky: 0, other: 0 };
    let mainPath = null, mainSize = 0;
    const riskyFiles = [];
    for (const f of files || []) {
      const cat = categorize(f.name || f.path);
      tot[cat] += f.size || 0;
      if (cat === "video" && (f.size || 0) > mainSize) {
        mainSize = f.size || 0;
        mainPath = f.path;
      }
      if (cat === "risky") riskyFiles.push(f.path);
    }
    const sum = Object.values(tot).reduce((a, b) => a + b, 0);
    // Open top-level folders by default
    const open = new Set(Object.values(tree.children).map((c) => c.id));
    return { totals: tot, total: sum, mainPlayablePath: mainPath, risky: riskyFiles, initialOpen: open };
  }, [files, tree]);

  const [expanded, setExpanded] = useState(initialOpen);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const rows = useMemo(() => flatten(tree, expanded), [tree, expanded]);

  if (!files || files.length === 0) {
    return <div className="text-xs text-[var(--tt-text-muted)] px-2">No file list available.</div>;
  }

  return (
    <div>
      <div className="mb-3">
        <BreakdownBar totals={totals} total={total} />
      </div>

      {risky.length > 0 && (
        <div
          data-testid="files-safety-warning"
          className="mb-3 flex items-start gap-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
        >
          <ShieldAlert className="w-4 h-4 shrink-0 text-red-400 mt-0.5" />
          <div className="min-w-0">
            <div className="font-bold uppercase tracking-wider text-[10px] mb-0.5">Executable files detected</div>
            <div className="text-red-200/80 break-all">
              This torrent contains {risky.length} potentially risky file{risky.length > 1 ? "s" : ""}
              {" "}({risky.slice(0, 3).map((p) => String(p).split("/").pop()).join(", ")}{risky.length > 3 ? "…" : ""}).
              Scan with antivirus before running.
            </div>
          </div>
        </div>
      )}

      <div className="max-h-72 overflow-y-auto pr-1" data-testid="files-tree">
        {rows.map((row) => {
          const pad = { paddingLeft: 8 + row.depth * 14 };
          if (row.kind === "folder") {
            return (
              <button
                key={`f-${row.id}`}
                type="button"
                onClick={() => toggle(row.id)}
                style={pad}
                data-testid={`tree-folder-${row.name}`}
                className="w-full flex items-center gap-1.5 text-xs py-1.5 rounded hover:bg-white/[0.05] transition-colors"
              >
                {row.isOpen
                  ? <ChevronDown className="w-3 h-3 text-[var(--tt-text-muted)] shrink-0" />
                  : <ChevronRight className="w-3 h-3 text-[var(--tt-text-muted)] shrink-0" />}
                {row.isOpen
                  ? <FolderOpen className="w-3.5 h-3.5 text-[var(--tt-yellow)] shrink-0" />
                  : <Folder className="w-3.5 h-3.5 text-[var(--tt-yellow)] shrink-0" />}
                <span className="font-mono-tt truncate flex-1 text-left">{row.name}/</span>
                <span className="text-[10px] text-[var(--tt-text-muted)] font-mono-tt pr-2">
                  {row.childCount} · {fmtBytes(row.size)}
                </span>
              </button>
            );
          }
          const isMain = row.fullPath === mainPlayablePath;
          const isNfo = row.category === "nfo";
          return (
            <div
              key={`x-${row.id}`}
              style={pad}
              data-testid={`tree-file-${row.category}`}
              className={`flex items-center gap-2 text-xs py-1 rounded hover:bg-white/[0.03] ${isMain ? "bg-[var(--tt-primary)]/[0.06]" : ""}`}
            >
              <span className="w-3 shrink-0" />
              {FILE_ICON[row.category]}
              <span className="flex-1 truncate font-mono-tt" title={row.name}>{row.name}</span>
              {isMain && (
                <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-[var(--tt-primary)]/50 text-[var(--tt-primary)] bg-[var(--tt-primary)]/10">
                  <Play className="w-2.5 h-2.5" /> Main
                </span>
              )}
              {isNfo && onViewNfo && (
                <button
                  onClick={onViewNfo}
                  data-testid="tree-view-nfo"
                  className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/15"
                >
                  View NFO
                </button>
              )}
              <span className="text-[var(--tt-text-muted)] shrink-0 min-w-[60px] text-right pr-2">{fmtBytes(row.size)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
